import { AvailableCountries, VATTypeEnum } from 'types/users.types';

import { LockClosedIcon } from '@heroicons/react/outline';
import invoiceBonusImgBE_EN from 'assets/images/wallet-onboarding-invoice-bonus-be-en.png';
import invoiceBonusImgBE_FR from 'assets/images/wallet-onboarding-invoice-bonus-be-fr.png';
import invoiceBonusImgBE_NL from 'assets/images/wallet-onboarding-invoice-bonus-be-nl.png';
import invoiceBonusImgDE_DE from 'assets/images/wallet-onboarding-invoice-bonus-de-de.png';
import invoiceBonusImgDE_EN from 'assets/images/wallet-onboarding-invoice-bonus-de-en.png';
import { Button } from 'components/atoms/Button';
import { Modal } from 'components/atoms/Modal';
import EuroCircleIcon from 'components/atoms/SVG/EuroCircleIcon.component';
import ModalHeading from 'components/molecules/ModalHeading';
import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import useIsMaxSubscriber from 'hooks/Subscriptions/useIsMaxSubsciber';
import { motion } from 'motion/react';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import type { AvailableLanguages } from 'types/users.types';
import { fadeTransition } from 'utils/constants';

const invoiceBonusImages: Record<AvailableCountries, Record<string, string>> = {
  [AvailableCountries.belgium]: {
    en: invoiceBonusImgBE_EN,
    fr: invoiceBonusImgBE_FR,
    nl: invoiceBonusImgBE_NL,
  },
  [AvailableCountries.germany]: {
    en: invoiceBonusImgDE_EN,
    de: invoiceBonusImgDE_DE,
  },
};

const InvoicesBonus: FC<{ onConfirm?: () => void }> = ({ onConfirm }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const user = useGetCurrentCustomerData().data;

  const isMaxSubscriber = useIsMaxSubscriber();

  return (
    <motion.div
      {...fadeTransition}
      className="flex items-center justify-center flex-col relative pb-20"
      key="invoice-bonus"
    >
      <ModalHeading
        Icon={() => <EuroCircleIcon className="-rotate-[18deg] scale-150" />}
        title={t('wallet.tutorial.invoices_bonus.title')}
        subtitle={
          isMaxSubscriber
            ? t('wallet.tutorial.invoices_bonus.subtitle')
            : t('wallet.tutorial.invoices_bonus.subtitle2')
        }
        subtitleClassName="w-[80%]"
        titleBadge={
          isMaxSubscriber
            ? undefined
            : {
                children:
                  user?.VATType === VATTypeEnum.subjectToVAT
                    ? t('subscriptions_screen.plans.pro_max')
                    : t('subscriptions_screen.plans.small_max'),
                size: 'md',
                color: 'bank',
                icon: LockClosedIcon,
              }
        }
      />

      <div className="flex flex-col items-center my-6">
        <img
          src={
            invoiceBonusImages[user?.country as AvailableCountries]?.[
              language as AvailableLanguages
            ]
          }
          className="w-[65%] mb-3"
        />
      </div>

      <Button
        color="bank"
        className="mt-6 w-[80%] absolute bottom-10 capitalize"
        onClick={onConfirm}
      >
        {t('next')}
      </Button>

      <Modal
        name="Accountable Banking Onboarding - Invoice bonus details"
        trigger={(modalApi) => (
          <Button
            color="bank"
            structure="text"
            className="mt-6 w-[80%] absolute bottom-0 capitalize"
            onClick={() => modalApi.openModal()}
          >
            {t('wallet.tutorial.invoices_bonus.cta')}
          </Button>
        )}
        className="!max-w-[40rem]"
        render={(api) => {
          return (
            <motion.div
              {...fadeTransition}
              className="flex items-center justify-center flex-col relative"
            >
              <ModalHeading
                Icon={() => (
                  <EuroCircleIcon className="-rotate-[18deg] scale-150" />
                )}
                title={t('wallet.tutorial.invoices_bonus.details_modal.title')}
              />

              <ReactMarkdown
                className="w-[80%] mx-auto"
                components={{
                  p: ({ children }) => <p className="mb-4">{children}</p>,
                }}
              >
                {t('wallet.tutorial.invoices_bonus.details_modal.content')}
              </ReactMarkdown>

              <Button
                color="bank"
                className="mt-6 w-[80%] "
                onClick={() => api.closeModal()}
              >
                {t('close')}
              </Button>
            </motion.div>
          );
        }}
      />
    </motion.div>
  );
};

export default InvoicesBonus;
